import React from "react"

import { useStaticQuery, graphql } from "gatsby"

const isTest = process.env.ENV_STATE === "test"

const socialLinks = [
  "https://www.facebook.com/firstfenceltd",
  "https://www.linkedin.com/company/firstfenceltd",
  "https://www.youtube.com/c/FirstfenceCoUk",
  "https://www.instagram.com/firstfenceltd",
  "https://twitter.com/firstfenceltd",
]

// Default depot trading hours (Mon–Fri 06:00–22:00, closed weekends).
const depotOpeningHours = {
  "@type": "OpeningHoursSpecification",
  dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
  opens: "06:00",
  closes: "22:00",
}

const SEO = ({
  title,
  description = "",
  metaTitle,
  meta = isTest
    ? [
        { name: "robots", content: "noindex" },
        { name: "googlebot", content: "noindex" },
      ]
    : [],
  metaImage,
  path,
  localBusiness,
  children,
}) => {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
          keywords
          siteUrl
        }
      }
    }
  `)

  const metaDescription = (description || site.siteMetadata.description).trim()
  const defaultTitle = "First Fence Ltd"
  // Trim: CMS titles occasionally carry stray whitespace/tabs
  const rawTitle = (metaTitle || title || "").trim()
  const formattedTitle = rawTitle ? `${rawTitle} | ${defaultTitle}` : defaultTitle

  let metaImgTags = []
  if (metaImage) {
    metaImgTags = [
      {
        property: "og:image",
        content: metaImage.url,
      },
      {
        property: "og:image:width",
        content: metaImage?.width,
      },
      {
        property: "og:image:height",
        content: metaImage?.height,
      },
      {
        name: "twitter:card",
        content: "summary_large_image",
      },
    ]
  } else {
    metaImgTags = [
      {
        name: "twitter:card",
        content: "summary",
      },
    ]
  }

  const metaTags = [
    ...meta,
    {
      name: `description`,
      content: metaDescription,
    },
    {
      name: `keywords`,
      content: site.siteMetadata.keywords.join(","),
    },
    // Facebook (og:)
    {
      property: `og:title`,
      content: formattedTitle,
    },
    {
      property: `og:description`,
      content: metaDescription,
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      name: `twitter:creator`,
      content: site.siteMetadata.author,
    },
    {
      name: `twitter:title`,
      content: formattedTitle,
    },
    {
      name: `twitter:description`,
      content: metaDescription,
    },
    ...metaImgTags,
  ]

  // Remove trailing and leading slashes from path
  const formattedPath = path?.replace(/^\/|\/$/g, "")

  return (
    <>
      <title>{formattedTitle}</title>
      {metaTags.map((metaTag, i) => {
        return <meta key={i} {...metaTag} />
      })}
      {path ? (
        <link
          rel="canonical"
          href={`${site.siteMetadata.siteUrl}/${formattedPath}`}
        />
      ) : null}
      {children}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://firstfence.co.uk/#org",
          name: "First Fence Ltd",
          url: "https://firstfence.co.uk/",
          logo: "https://firstfence.co.uk/favicon.ico",
          email: "sales@firstfence.co.uk",
          telephone: "+44 1283 512111",
          foundingDate: "2010",
          legalName: "First Fence Limited",
          identifier: [
            {
              "@type": "PropertyValue",
              name: "Company Number",
              value: "07142033",
            },
            {
              "@type": "PropertyValue",
              name: "VAT Number",
              value: "GB 985368854",
            },
          ],
          address: {
            "@type": "PostalAddress",
            streetAddress: "Kiln Way, Woodville",
            addressLocality: "Swadlincote",
            addressRegion: "Derbyshire",
            postalCode: "DE11 8EA",
            addressCountry: "GB",
          },
          areaServed: { "@type": "Country", name: "United Kingdom" },
          sameAs: socialLinks,
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(
          localBusiness
            ? {
                "@context": "https://schema.org",
                "@type": "LocalBusiness",
                "@id": localBusiness.id,
                name: localBusiness.name,
                image: localBusiness.image,
                url: localBusiness.url,
                telephone: localBusiness.telephone,
                email: "sales@firstfence.co.uk",
                branchOf: { "@id": "https://firstfence.co.uk/#org" },
                address: {
                  "@type": "PostalAddress",
                  streetAddress: localBusiness.streetAddress,
                  addressLocality: localBusiness.addressLocality,
                  addressRegion: localBusiness.addressRegion,
                  postalCode: localBusiness.postalCode,
                  addressCountry: "GB",
                },
                geo: {
                  "@type": "GeoCoordinates",
                  latitude: localBusiness.latitude,
                  longitude: localBusiness.longitude,
                },
                openingHoursSpecification:
                  localBusiness.openingHours || depotOpeningHours,
                priceRange: "$$",
                sameAs: socialLinks,
              }
            : {
                "@context": "https://schema.org",
                "@type": "LocalBusiness",
                "@id": "https://firstfence.co.uk/#localbusiness",
                name: "First Fence Ltd",
                image: "https://firstfence.co.uk/logo.png",
                url: "https://firstfence.co.uk/",
                telephone: "+44 1283 512111",
                email: "sales@firstfence.co.uk",
                address: {
                  "@type": "PostalAddress",
                  streetAddress: "Kiln Way, Woodville",
                  addressLocality: "Swadlincote",
                  addressRegion: "Derbyshire",
                  postalCode: "DE11 8EA",
                  addressCountry: "GB",
                },
                geo: {
                  "@type": "GeoCoordinates",
                  latitude: 52.7737,
                  longitude: -1.5391,
                },
                openingHoursSpecification: {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: [
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                  ],
                  opens: "06:00",
                  closes: "22:00",
                },
                priceRange: "$$",
                sameAs: socialLinks,
              }
        )}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://firstfence.co.uk/#website",
          url: "https://firstfence.co.uk/",
          name: "First Fence Ltd",
          publisher: { "@id": "https://firstfence.co.uk/#org" },
          inLanguage: "en-GB",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://firstfence.co.uk/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        })}
      </script>
    </>
  )
}

export default SEO
